import { React, useState, useEffect, useRef, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { ArrowRight, Vote, ChevronLeft, ChevronRight, ArrowRightLeft, HandHeart, ReceiptText, Store } from "lucide-react"
import WalletList from "./WalletList"
import coinImg from "../../resources/Sheaf_2.png"
import { getSheafBalance, transferSheafToUser, transferSheafToGroup } from "../../Services/SheafService"
import DashboardMemberships from "./DashboardMemberships"
import DashboardRoles from "./DashboardRoles"
import DashboardActivityPanel from "./DashboardActivityPanel"
import JobApplications from "./JobApplications"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"

const MetricCard = ({ icon, title, value, to, actionLabel }) => {
  const content = (
    <Card className={`h-full border-white/10 bg-slate-950/50 shadow-[0_18px_55px_rgba(0,0,0,0.35)] backdrop-blur transition ${to ? "group-hover:border-cyan-200/50 group-hover:bg-slate-900/70" : ""}`}>
      <CardContent className="flex min-h-[96px] items-center gap-4 p-4">
      <div className="rounded-xl border border-white/10 bg-slate-900/80 p-2.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-300">{title}</p>
        <h3 className="text-2xl font-bold text-slate-50">{value}</h3>
        {actionLabel && <p className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-cyan-200">{actionLabel}<ArrowRight className="h-4 w-4" /></p>}
      </div>
    </CardContent>
  </Card>
  )

  if (to) {
    return (
      <Link to={to} className="group block rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-950">
        {content}
      </Link>
    )
  }

  return content
}

const Dashboard = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL
  const campaignVotingEnabled = isFeatureEnabled(FEATURE_FLAGS.campaignVoting)
  const marketplaceTradesEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceTrades)
  const bitcoinTransactionsEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const sheafPurchaseEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase)
  const jobsPayrollEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll)

  const [metrics, setMetrics] = useState({
    totalVotes: 0,
    sheafBalance: 0,
  })
  const [recipientUser, setRecipientUser] = useState("")
  const [userAmount, setUserAmount] = useState("")
  const [groupId, setGroupId] = useState("")
  const [groupAmount, setGroupAmount] = useState("")
  const [inventoryAssets, setInventoryAssets] = useState([])
  const [isInventoryLoading, setIsInventoryLoading] = useState(false)
  const [tradeSummary, setTradeSummary] = useState({
    trades: 0,
    pendingTrades: 0,
    transactions: 0,
  })
  const inventoryCarouselRef = useRef(null)

  const fetchVoteCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/Votes/user/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const count = await response.json()
        setMetrics((prev) => ({ ...prev, totalVotes: count }))
      }
    } catch (error) {
      console.error("Error fetching vote count:", error)
    }
  }, [apiUrl, getAccessTokenSilently])

  const fetchInventoryAssets = useCallback(async () => {
    if (!user) {
      return
    }

    setIsInventoryLoading(true)
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/assets/user/${user.sub}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const assets = await response.json()
        setInventoryAssets(Array.isArray(assets) ? assets : [])
      } else {
        setInventoryAssets([])
      }
    } catch (error) {
      console.error("Error fetching inventory assets:", error)
      setInventoryAssets([])
    } finally {
      setIsInventoryLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, user])

  const scrollInventory = (direction) => {
    if (!inventoryCarouselRef.current) {
      return
    }

    inventoryCarouselRef.current.scrollBy({
      left: direction * 140,
      behavior: "smooth",
    })
  }

  const fetchTradeSummary = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const headers = {
        Authorization: `Bearer ${token}`,
      }
      const [tradeResponse, transactionResponse] = await Promise.all([
        marketplaceTradesEnabled ? fetch(`${apiUrl}/api/trades`, { headers }) : Promise.resolve(null),
        (marketplaceTradesEnabled || bitcoinTransactionsEnabled) ? fetch(`${apiUrl}/api/transactions`, { headers }) : Promise.resolve(null),
      ])

      const trades = tradeResponse?.ok ? await tradeResponse.json() : []
      const transactions = transactionResponse?.ok ? await transactionResponse.json() : []
      const safeTrades = Array.isArray(trades) ? trades : []
      const safeTransactions = Array.isArray(transactions) ? transactions : []

      setTradeSummary({
        trades: safeTrades.length,
        pendingTrades: safeTrades.filter((trade) => String(trade.status || "").toLowerCase() === "pending").length,
        transactions: safeTransactions.length,
      })
    } catch (error) {
      console.error("Error fetching trade summary:", error)
      setTradeSummary({
        trades: 0,
        pendingTrades: 0,
        transactions: 0,
      })
    }
  }, [apiUrl, bitcoinTransactionsEnabled, getAccessTokenSilently, marketplaceTradesEnabled])

  const fetchSheaf = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const balance = await getSheafBalance(token)
      setMetrics((prev) => ({ ...prev, sheafBalance: balance }))
    } catch (err) {
      console.error("Error fetching Sheaf balance:", err)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    if (user) {
      if (campaignVotingEnabled) fetchVoteCount()
      if (sheafPurchaseEnabled) fetchSheaf()
      if (marketplaceTradesEnabled) fetchInventoryAssets()
      if (marketplaceTradesEnabled || bitcoinTransactionsEnabled) fetchTradeSummary()
    }
  }, [
    bitcoinTransactionsEnabled,
    campaignVotingEnabled,
    fetchInventoryAssets,
    fetchSheaf,
    fetchTradeSummary,
    fetchVoteCount,
    marketplaceTradesEnabled,
    sheafPurchaseEnabled,
    user,
  ])

  const handleSendUser = async (e) => {
    e.preventDefault()
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      await transferSheafToUser(token, recipientUser, parseFloat(userAmount))
      setRecipientUser("")
      setUserAmount("")
      fetchSheaf()
    } catch (err) {
      console.error("Error sending Sheaf:", err)
    }
  }

  const handleSendGroup = async (e) => {
    e.preventDefault()
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      await transferSheafToGroup(token, parseInt(groupId), parseFloat(groupAmount))
      setGroupId("")
      setGroupAmount("")
      fetchSheaf()
    } catch (err) {
      console.error("Error sending Sheaf to group:", err)
    }
  }

  if (isLoading) {
    return <div>Loading ...</div>
  }

  if (!isAuthenticated || !user) {
    return <div>Not authenticated</div>
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_15%_0%,rgba(90,214,255,0.12),transparent_30%),radial-gradient(circle_at_85%_10%,rgba(244,199,93,0.12),transparent_35%),linear-gradient(180deg,#080b17_0%,#0f1322_45%,#090d18_100%)] px-4 pb-10 pt-6 text-slate-100">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-8 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/50 shadow-[0_25px_90px_rgba(0,0,0,0.45)] backdrop-blur">
          <div className="relative overflow-hidden border-b border-white/10 px-6 py-7 md:px-8">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(96,165,250,0.18),transparent_35%),radial-gradient(circle_at_85%_8%,rgba(251,191,36,0.2),transparent_40%)]" />
            <div className="relative flex flex-col justify-between gap-5 md:flex-row md:items-center">
              <div>
                <p className="mb-2 inline-flex rounded-full border border-amber-200/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-200">
                  Kingdom Command
                </p>
                <h1 className="text-3xl font-bold md:text-4xl">Dashboard</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
                  Track your impact, manage Sheaf activity, and stay on top of your memberships and opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <MetricCard icon={<HandHeart className="h-8 w-8 text-amber-200" />} title="Needs" value="Post or answer" to="/needs" actionLabel="Find where help is needed" />
          {campaignVotingEnabled && <MetricCard icon={<Vote className="h-8 w-8 text-sky-300" />} title="Total Votes" value={metrics.totalVotes} to="/dashboard/votes" actionLabel="View history" />}
          {sheafPurchaseEnabled && <MetricCard icon={<img src={coinImg} alt="Sheaf" className="h-8 w-8" />} title="Sheaf Balance" value={metrics.sheafBalance} />}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-12">
            <DashboardActivityPanel
              inventoryCount={inventoryAssets.length}
              isInventoryLoading={isInventoryLoading}
              jobsPayrollEnabled={jobsPayrollEnabled}
              marketplaceTradesEnabled={marketplaceTradesEnabled}
              totalVotes={metrics.totalVotes}
            />
          </div>

          <div className="lg:col-span-3">
            <DashboardMemberships />
          </div>

          <Card className="border-white/10 bg-slate-950/55 shadow-[0_20px_70px_rgba(0,0,0,0.35)] lg:col-span-6">
            <CardHeader>
              <CardTitle className="text-slate-100">Positions/Roles</CardTitle>
            </CardHeader>
            <CardContent>
              <DashboardRoles />
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-slate-950/55 shadow-[0_20px_70px_rgba(0,0,0,0.35)] lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-slate-100">Trade</CardTitle>
            </CardHeader>
            <CardContent className="text-slate-200">
              {sheafPurchaseEnabled && <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200/20 bg-amber-400/10 p-2 text-sm font-medium text-amber-100">
                <img src={coinImg} alt="Sheaf" className="h-5 w-5" />
                <span>Sheaf Balance: {metrics.sheafBalance}</span>
              </div>}
              {sheafPurchaseEnabled && <details className="mb-4 rounded-lg border border-white/15 bg-slate-900/65 p-3">
                <summary className="cursor-pointer font-semibold text-slate-100">Send Sheaf</summary>
                <div className="mt-3 space-y-4">
                  <form onSubmit={handleSendUser} className="mb-4 space-y-2">
                    <input
                      type="text"
                      placeholder="Recipient username"
                      value={recipientUser}
                      onChange={(e) => setRecipientUser(e.target.value)}
                      className="w-full rounded-md border border-white/15 bg-slate-900 p-2 text-slate-100 placeholder:text-slate-400"
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={userAmount}
                      onChange={(e) => setUserAmount(e.target.value)}
                      className="w-full rounded-md border border-white/15 bg-slate-900 p-2 text-slate-100 placeholder:text-slate-400"
                    />
                    <Button type="submit" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90">
                      Send to User
                    </Button>
                  </form>
                  <form onSubmit={handleSendGroup} className="space-y-2">
                    <input
                      type="text"
                      placeholder="Group ID"
                      value={groupId}
                      onChange={(e) => setGroupId(e.target.value)}
                      className="w-full rounded-md border border-white/15 bg-slate-900 p-2 text-slate-100 placeholder:text-slate-400"
                    />
                    <input
                      type="number"
                      placeholder="Amount"
                      value={groupAmount}
                      onChange={(e) => setGroupAmount(e.target.value)}
                      className="w-full rounded-md border border-white/15 bg-slate-900 p-2 text-slate-100 placeholder:text-slate-400"
                    />
                    <Button type="submit" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90">
                      Send to Group
                    </Button>
                  </form>
                </div>
              </details>}
              <ul className="space-y-4 text-slate-300">
                {bitcoinTransactionsEnabled && (
                  <li>
                    <WalletList />
                  </li>
                )}
                {marketplaceTradesEnabled && <li>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-slate-100">Inventory</h4>
                    <Link to={`/user-inventory/user/${user.sub}`} className="text-sm font-medium text-cyan-300 transition hover:text-cyan-200 hover:underline">
                      View all
                    </Link>
                  </div>
                  {isInventoryLoading ? (
                    <p className="text-sm text-slate-400">Loading inventory...</p>
                  ) : inventoryAssets.length === 0 ? (
                    <p className="text-sm text-slate-400">No inventory items found.</p>
                  ) : (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => scrollInventory(-1)}
                        className="absolute -left-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/20 bg-slate-900/90 p-1 text-slate-200 transition hover:bg-slate-800"
                        aria-label="Scroll inventory left"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      <div
                        ref={inventoryCarouselRef}
                        className="flex snap-x snap-mandatory gap-3 overflow-x-auto rounded-lg border border-white/10 bg-slate-900/50 p-3"
                      >
                        {inventoryAssets.map((asset) => (
                          <div
                            key={asset.id}
                            className="w-36 min-w-36 snap-start overflow-hidden rounded-lg border border-white/10 bg-slate-950/70"
                          >
                            <img
                              src={asset.imageURL}
                              alt={asset.assetName || "Inventory item"}
                              className="h-20 w-full object-cover"
                            />
                            <div className="border-t border-white/10 px-2 py-2 text-xs text-slate-200">
                              <span className="block truncate font-semibold">{asset.assetName || "Unnamed item"}</span>
                              <Link
                                to={`/user-inventory/user/${user.sub}?listAssetId=${asset.id}`}
                                className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-amber-200/50 bg-amber-300/15 px-2 py-1.5 text-[0.72rem] font-bold text-amber-100 transition hover:border-amber-100 hover:bg-amber-300/25 hover:text-white focus:outline-none focus:ring-2 focus:ring-amber-200"
                                aria-label={`Make ${asset.assetName || "this inventory item"} for sale or trade`}
                              >
                                <Store className="h-3.5 w-3.5" /> List item
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => scrollInventory(1)}
                        className="absolute -right-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/20 bg-slate-900/90 p-1 text-slate-200 transition hover:bg-slate-800"
                        aria-label="Scroll inventory right"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </li>}
                {(marketplaceTradesEnabled || bitcoinTransactionsEnabled) && (
                  <li>
                    <Link
                      to="/dashboard/trading-history"
                      className="group block rounded-xl border border-cyan-200/20 bg-cyan-400/10 p-3 text-slate-100 transition hover:border-cyan-100/50 hover:bg-cyan-400/15 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-cyan-100">
                            <ArrowRightLeft className="h-4 w-4" /> Trading history
                          </p>
                          <p className="mt-2 text-sm leading-5 text-slate-300">
                            Timeline of your purchases, sales, and trade offers.
                          </p>
                        </div>
                        <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-cyan-200 transition group-hover:translate-x-1" />
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                        <span className="rounded-lg border border-white/10 bg-slate-950/55 p-2">
                          <strong className="block text-lg text-white">{tradeSummary.transactions}</strong>
                          BTC txns
                        </span>
                        <span className="rounded-lg border border-white/10 bg-slate-950/55 p-2">
                          <strong className="block text-lg text-white">{tradeSummary.trades}</strong>
                          trades
                        </span>
                        <span className="rounded-lg border border-white/10 bg-slate-950/55 p-2">
                          <strong className="block text-lg text-white">{tradeSummary.pendingTrades}</strong>
                          pending
                        </span>
                      </div>
                    </Link>
                  </li>
                )}
                <li className="rounded-xl border border-white/10 bg-slate-900/45 p-3">
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-100">
                    <ReceiptText className="h-4 w-4 text-amber-200" /> Disputes
                  </p>
                  <p className="mt-1 text-sm text-slate-400">Dispute records will appear with related trades when that workflow is enabled.</p>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-slate-950/55 shadow-[0_20px_70px_rgba(0,0,0,0.35)] lg:col-span-9">
            <CardHeader>
              <CardTitle className="text-slate-100">Job Applications</CardTitle>
            </CardHeader>
            <CardContent>
              {jobsPayrollEnabled && <JobApplications />}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
